"use client";

import { Button } from "@speakai/ui/button";
import { Switch } from "@speakai/ui/switch";
import { Badge } from "@speakai/ui/badge";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";
import type { Field } from "@/features/fields/api";

interface FolderDialogStepAutomationProps {
  selectedFieldIds: Set<string>;
  selectedFields: Field[];
  automationFieldIds: Set<string>;
  allAutomationEnabled: boolean;
  handleToggleAllAutomation: (enabled: boolean) => void;
  handleToggleAutomationField: (fieldId: string) => void;
  handleSetupAutomation: () => void | Promise<void>;
  finishDialog: () => void;
  automationPending: boolean;
}

export function FolderDialogStepAutomation({
  selectedFieldIds,
  selectedFields,
  automationFieldIds,
  allAutomationEnabled,
  handleToggleAllAutomation,
  handleToggleAutomationField,
  handleSetupAutomation,
  finishDialog,
  automationPending,
}: FolderDialogStepAutomationProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        {t("FOLDERS.AUTOMATION_DESC")}
      </p>

      {selectedFieldIds.size === 0 ? (
        <p className="text-sm text-muted-foreground px-1">
          No fields were selected in Step 2. Finish to create the folder without automation.
        </p>
      ) : (
        <>
          {/* Master toggle */}
          <div className="flex items-center justify-between py-2 px-3 rounded-lg border border-border">
            <span className="text-sm font-medium">
              {t("FOLDERS.ENABLE_AI_ALL")}
            </span>
            <Switch
              checked={allAutomationEnabled}
              onChange={(v) => handleToggleAllAutomation(!!v)}
            />
          </div>

          {/* Individual field toggles */}
          <div className="flex flex-col gap-1 max-h-48 overflow-y-auto" data-sentry-mask>
            {selectedFields.map((field) => (
              <div
                key={field.id}
                className="flex items-center justify-between py-2 px-3 rounded hover:bg-muted/50"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm">{field.name}</span>
                  <Badge variant="outline" className="text-xs">
                    {field.type}
                  </Badge>
                </div>
                <Switch
                  checked={automationFieldIds.has(field.id)}
                  onChange={() => handleToggleAutomationField(field.id)}
                />
              </div>
            ))}
          </div>
        </>
      )}

      <div className="flex justify-end pt-2">
        <Button
          type="button"
          onClick={automationFieldIds.size > 0 ? handleSetupAutomation : finishDialog}
          disabled={automationPending}
        >
          {automationPending
            ? t("ACTION.SETTING_UP")
            : automationFieldIds.size > 0
              ? t("FOLDERS.SETUP_AUTOMATION")
              : "Finish"}
          {!automationPending && automationFieldIds.size > 0 && (
            <Sparkles className="h-4 w-4 ml-1" />
          )}
        </Button>
      </div>
    </div>
  );
}
